<template>
  <div class="customer-category-mapping-detail">
    <div class="detail-header">
      <div>
        <a class="back-link" @click="state.goBack">← 返回映射列表</a>
        <h2>编辑映射：{{ state.detail.mappingName || '-' }}</h2>
        <div class="subtitle">
          绑定类目树：{{ state.detail.treeName || '-' }} · 映射覆盖率：
          {{ state.coverageText }} · 状态：{{ state.statusText }}
        </div>
      </div>
      <a-button @click="state.openBasicEdit">编辑基本信息</a-button>
    </div>

    <div class="ims-container">
      <div class="toolbar">
        <div class="toolbar__filters">
          <a-select
            v-model:value="state.filters.systemLevelOneId"
            allow-clear
            placeholder="全部系统一级"
            style="width: 180px"
          >
            <a-select-option
              v-for="item in state.levelOneOptions"
              :key="item.id"
              :value="item.id"
            >
              {{ item.name }}
            </a-select-option>
          </a-select>
          <a-select
            v-model:value="state.filters.mappingState"
            allow-clear
            placeholder="全部状态"
            style="width: 130px"
          >
            <a-select-option value="MAPPED">已映射</a-select-option>
            <a-select-option value="UNMAPPED">未映射</a-select-option>
          </a-select>
          <a-input-search
            v-model:value="state.filters.keyword"
            placeholder="搜索系统类目..."
            style="width: 240px"
          />
        </div>
        <div>
          <a-button
            class="mr10"
            :disabled="!state.selectedRowKeys.length"
            @click="state.openBatchMapping"
          >
            批量映射
          </a-button>
        </div>
      </div>

      <a-table
        row-key="systemCategoryId"
        bordered
        :columns="state.columns"
        :data-source="state.filteredDataSource"
        :loading="state.loading"
        :pagination="false"
        size="small"
        :row-selection="{
          selectedRowKeys: state.selectedRowKeys,
          onChange: state.selectionChange
        }"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'customerCategoryPath'">
            <a-tag v-if="state.isMapped(record)" color="blue">
              {{ record.customerCategoryPath }}
            </a-tag>
            <a-tag v-else>未映射</a-tag>
          </template>
          <template v-else-if="column.dataIndex === 'action'">
            <a @click="state.openMapping(record)">
              {{ state.isMapped(record) ? '修改' : '映射' }}
            </a>
          </template>
        </template>
      </a-table>
    </div>

    <a-modal
      v-model:visible="state.mappingVisible"
      :title="state.mappingRows.length > 1 ? '批量映射' : '设置映射'"
      :footer="null"
      @cancel="state.closeMapping"
    >
      <a-alert
        class="mb16"
        type="info"
        :message="`已选择 ${state.mappingRows.length} 个系统三级类目`"
        show-icon
      />
      <a-form layout="vertical">
        <a-form-item label="目标对客三级类目" required>
          <a-select
            v-model:value="state.targetCustomerCategoryId"
            show-search
            option-filter-prop="label"
            placeholder="请选择对客三级类目"
            :loading="state.customerCategoryLoading"
            :not-found-content="
              state.customerCategoryLoading ? '加载中...' : '暂无可选类目'
            "
          >
            <a-select-option
              v-for="item in state.customerCategoryOptions"
              :key="item.id"
              :value="item.id"
              :label="item.path || item.name"
            >
              {{ item.path || item.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
      </a-form>
      <div class="modal-footer">
        <a-button @click="state.closeMapping">取消</a-button>
        <a-button
          type="primary"
          :loading="state.submitting"
          @click="state.confirmMapping"
        >
          保存映射
        </a-button>
      </div>
    </a-modal>

    <a-modal
      v-model:visible="state.basicEditVisible"
      title="编辑映射基本信息"
      :footer="null"
      @cancel="state.basicEditVisible = false"
    >
      <a-form layout="vertical">
        <a-form-item label="映射名称" required>
          <a-input v-model:value="state.basicForm.mappingName" />
        </a-form-item>
        <a-form-item label="描述">
          <a-textarea v-model:value="state.basicForm.description" :rows="3" />
        </a-form-item>
        <a-form-item label="绑定对客类目树" required>
          <a-select
            v-model:value="state.basicForm.treeId"
            placeholder="请选择对客类目树"
            :loading="state.treeLoading"
            :not-found-content="state.treeLoading ? '加载中...' : '暂无数据'"
          >
            <a-select-option
              v-for="item in state.treeOptions"
              :key="item.id"
              :value="item.id"
            >
              {{ item.treeName }}
            </a-select-option>
          </a-select>
        </a-form-item>
      </a-form>
      <div class="modal-footer">
        <a-button @click="state.basicEditVisible = false">取消</a-button>
        <a-button type="primary" @click="state.saveBasicInfo">保存</a-button>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { computed, onMounted, reactive, toRefs, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { message } from 'ant-design-vue'
import {
  getCustomerCategoryTree,
  listCustomerCategoryTrees
} from '../CustomerCategoryTreeConfig/service'
import {
  batchUpsertCustomerCategoryMappingRules,
  getCustomerCategoryMapping,
  getSelectableCustomerCategories,
  listCustomerCategoryMappingRules,
  mappingStatusText,
  updateCustomerCategoryMapping,
  upsertCustomerCategoryMappingRule
} from '../CustomerCategoryMappingConfig/service'

export default {
  name: 'CustomerCategoryMappingConfigDetail',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const state = reactive({
      mappingId: '',
      detail: {},
      dataSource: [],
      loading: false,
      submitting: false,
      filters: {
        systemLevelOneId: undefined,
        mappingState: undefined,
        keyword: ''
      },
      columns: [
        { title: '系统一级', dataIndex: 'systemLevelOneName' },
        { title: '系统二级', dataIndex: 'systemLevelTwoName' },
        { title: '系统三级', dataIndex: 'systemLevelThreeName' },
        { title: '英文名', dataIndex: 'systemCategoryEnglishName' },
        { title: '客类目', dataIndex: 'customerCategoryPath' },
        { title: '操作', dataIndex: 'action', width: 120, align: 'center' }
      ],
      selectedRowKeys: [],
      selectedRows: [],
      customerCategoryOptions: [],
      customerCategoryLoading: false,
      treeOptions: [],
      treeLoading: false,
      mappingVisible: false,
      mappingRows: [],
      targetCustomerCategoryId: undefined,
      basicEditVisible: false,
      basicForm: { mappingName: '', description: '', treeId: undefined },
      statusText: computed(() => mappingStatusText(state.detail.status)),
      coverageText: computed(() => {
        if (state.detail.coverageRate !== undefined) {
          const rate = Number(state.detail.coverageRate)
          const percentage = rate <= 1 ? rate * 100 : rate
          return `${percentage.toFixed(2)}%`
        }
        return `${state.detail.mappedCount || 0}/${state.detail.totalCount ||
          0}`
      }),
      levelOneOptions: computed(() => {
        const result = new Map()
        state.dataSource.forEach(item => {
          if (item.systemLevelOneId && item.systemLevelOneName) {
            result.set(item.systemLevelOneId, item.systemLevelOneName)
          }
        })
        return [...result].map(([id, name]) => ({ id, name }))
      }),
      filteredDataSource: computed(() => {
        const keyword = state.filters.keyword.trim().toLowerCase()
        return state.dataSource.filter(item => {
          if (
            state.filters.systemLevelOneId !== undefined &&
            state.filters.systemLevelOneId !== '' &&
            `${item.systemLevelOneId}` !== `${state.filters.systemLevelOneId}`
          ) {
            return false
          }
          if (
            state.filters.mappingState &&
            item.mappingState !== state.filters.mappingState
          ) {
            return false
          }
          if (!keyword) return true
          return [
            item.systemLevelOneName,
            item.systemLevelTwoName,
            item.systemLevelThreeName,
            item.systemCategoryEnglishName,
            item.customerCategoryPath
          ]
            .join(' ')
            .toLowerCase()
            .includes(keyword)
        })
      }),
      isMapped: record =>
        record.mappingState === 'MAPPED' ||
        Boolean(record.customerCategoryNodeId),
      loadDetail: async () => {
        const detail = await getCustomerCategoryMapping(state.mappingId)
        if (!detail) {
          message.error('未找到对应映射')
          state.goBack()
          return
        }
        state.detail = detail
        if (!state.detail.treeName && state.detail.treeId) {
          const tree = await getCustomerCategoryTree(state.detail.treeId)
          state.detail.treeName = tree?.treeName || `#${state.detail.treeId}`
        }
      },
      loadCustomerCategories: async () => {
        state.customerCategoryOptions = []
        if (!state.mappingId) {
          message.error('缺少当前映射 ID，无法查询目标类目')
          return
        }
        state.customerCategoryLoading = true
        try {
          state.customerCategoryOptions = await getSelectableCustomerCategories(
            state.mappingId
          )
        } catch (error) {
          state.customerCategoryOptions = []
          message.error(error.message || '可选对客三级类目加载失败')
        } finally {
          state.customerCategoryLoading = false
        }
      },
      loadTrees: async () => {
        state.treeLoading = true
        state.treeOptions = []
        try {
          const result = await listCustomerCategoryTrees({
            brandId: state.detail.brandId,
            channelId: state.detail.channelId,
            siteId: state.detail.siteId,
            terminalId: state.detail.terminalId,
            status: '0',
            pageNum: 1,
            pageSize: 9999
          })
          state.treeOptions = result.list
        } finally {
          state.treeLoading = false
        }
      },
      loadRules: async () => {
        state.loading = true
        try {
          state.dataSource = await listCustomerCategoryMappingRules(
            state.mappingId
          )
          state.selectedRowKeys = []
          state.selectedRows = []
        } catch (error) {
          message.error(error.message || '映射规则加载失败')
        } finally {
          state.loading = false
        }
      },
      selectionChange: (keys, rows) => {
        state.selectedRowKeys = keys
        state.selectedRows = rows
      },
      openMapping: async record => {
        state.mappingRows = [record]
        state.targetCustomerCategoryId = record.customerCategoryNodeId
        state.mappingVisible = true
        await state.loadCustomerCategories()
      },
      openBatchMapping: async () => {
        state.mappingRows = [...state.selectedRows]
        state.targetCustomerCategoryId = undefined
        state.mappingVisible = true
        await state.loadCustomerCategories()
      },
      closeMapping: () => {
        state.mappingVisible = false
        state.mappingRows = []
        state.targetCustomerCategoryId = undefined
      },
      confirmMapping: async () => {
        if (!state.targetCustomerCategoryId) {
          message.error('请选择目标对客三级类目')
          return
        }
        state.submitting = true
        try {
          if (state.mappingRows.length === 1) {
            await upsertCustomerCategoryMappingRule(state.mappingId, {
              categoryL3Id:
                state.mappingRows[0].categoryL3Id ??
                state.mappingRows[0].systemCategoryId,
              customerCategoryNodeId: state.targetCustomerCategoryId
            })
          } else {
            await batchUpsertCustomerCategoryMappingRules(
              state.mappingId,
              state.mappingRows.map(item => ({
                categoryL3Id: item.categoryL3Id ?? item.systemCategoryId,
                customerCategoryNodeId: state.targetCustomerCategoryId
              }))
            )
          }
          message.success('映射保存成功')
          state.closeMapping()
          await Promise.all([state.loadDetail(), state.loadRules()])
        } catch (error) {
          message.error(error.message || '映射保存失败')
        } finally {
          state.submitting = false
        }
      },
      openBasicEdit: async () => {
        state.basicForm = {
          mappingName: state.detail.mappingName,
          description: state.detail.description,
          treeId: state.detail.treeId
        }
        state.basicEditVisible = true
        try {
          await state.loadTrees()
        } catch (error) {
          message.error(error.message || '对客类目树加载失败')
        }
      },
      saveBasicInfo: async () => {
        if (!state.basicForm.mappingName || !state.basicForm.treeId) {
          message.error('请填写映射名称并选择对客类目树')
          return
        }
        try {
          await updateCustomerCategoryMapping(state.mappingId, state.basicForm)
          message.success('基本信息保存成功')
          state.basicEditVisible = false
          await state.loadDetail()
        } catch (error) {
          message.error(error.message || '基本信息保存失败')
        }
      },
      goBack: () => router.push({ name: 'CustomerCategoryMappingConfig' })
    })

    watch(
      () => [
        state.filters.systemLevelOneId,
        state.filters.mappingState,
        state.filters.keyword
      ],
      () => {
        state.selectedRowKeys = []
        state.selectedRows = []
      }
    )

    onMounted(async () => {
      state.mappingId = route.query.id
      try {
        await state.loadDetail()
        await state.loadRules()
      } catch (error) {
        message.error(error.message || '映射详情初始化失败')
      }
    })

    return { ...toRefs(state), state }
  }
}
</script>

<style lang="scss" scoped>
.customer-category-mapping-detail {
  min-height: 100vh;
  padding: 16px;
  background: #f5f7fa;
}

.detail-header,
.toolbar,
.toolbar__filters,
.modal-footer {
  display: flex;
  align-items: center;
}

.detail-header,
.toolbar {
  justify-content: space-between;
}

.detail-header {
  padding: 20px;
  background: #fff;
}

.detail-header h2 {
  margin: 8px 0 4px;
}

.back-link {
  color: #4080ff;
}

.subtitle {
  color: #8c8c8c;
}

.toolbar {
  margin-bottom: 16px;
}

.toolbar__filters {
  gap: 10px;
}

.modal-footer {
  justify-content: flex-end;
}

.modal-footer {
  gap: 8px;
}

.mr10 {
  margin-right: 10px;
}

.ml10 {
  margin-left: 10px;
}

.mb16 {
  margin-bottom: 16px;
}
</style>
